import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

const CONTROL_STYLE = {
  padding: '7px 12px',
  borderRadius: 6,
  border: '1px solid #E2E5EC',
  background: '#fff',
  color: '#021344',
  fontSize: '0.8125rem',
  cursor: 'pointer',
  outline: 'none',
  fontFamily: 'inherit',
};

/**
 * One channel row: name + clickable stream URL.
 */
function ChannelRow({ channel, index }) {
  return (
    <tr style={{ background: index % 2 === 0 ? '#fff' : '#F8F9FC' }}>
      <td style={{ padding: '10px 14px', fontSize: '0.875rem', color: '#021344', fontWeight: 500, borderBottom: '1px solid #F0F2F8' }}>
        {channel.name || '—'}
      </td>
      <td style={{ padding: '10px 14px', fontSize: '0.8125rem', borderBottom: '1px solid #F0F2F8' }}>
        {channel.stream_url ? (
          <a
            href={channel.stream_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1F50FF', textDecoration: 'none', wordBreak: 'break-all' }}
          >
            {channel.stream_url}
          </a>
        ) : (
          <span style={{ color: '#9AA3B5' }}>—</span>
        )}
      </td>
    </tr>
  );
}

ChannelRow.propTypes = {
  channel: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    stream_url: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
};

/**
 * A tenant's card: header with name + channel count, table of channels below.
 */
function TenantCard({ tenant }) {
  const channels = tenant.channels || [];

  return (
    <div style={{
      background: '#fff',
      borderRadius: 10,
      border: '1px solid #E2E5EC',
      boxShadow: '0 1px 3px rgba(2,19,68,0.06)',
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: '1px solid #E2E5EC',
        background: '#F8F9FC',
      }}>
        <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#021344' }}>
          {tenant.tenant_name}
        </span>
        <span style={{
          fontSize: '0.75rem',
          fontWeight: 600,
          padding: '2px 10px',
          borderRadius: 9999,
          background: '#EEF2FF',
          color: '#1F50FF',
          border: '1px solid rgba(31,80,255,0.20)',
        }}>
          {channels.length} {channels.length === 1 ? 'canal' : 'canales'}
        </span>
      </div>

      {channels.length === 0 ? (
        <div style={{ padding: '20px 16px', textAlign: 'center', color: '#9AA3B5', fontSize: '0.8125rem' }}>
          Sin canales configurados.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{
                padding: '8px 14px', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: '#6B7A99', textAlign: 'left', borderBottom: '2px solid #E2E5EC',
              }}>
                Canal
              </th>
              <th style={{
                padding: '8px 14px', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: '#6B7A99', textAlign: 'left', borderBottom: '2px solid #E2E5EC',
              }}>
                Stream URL
              </th>
            </tr>
          </thead>
          <tbody>
            {channels.map((c, i) => <ChannelRow key={c.id ?? i} channel={c} index={i} />)}
          </tbody>
        </table>
      )}
    </div>
  );
}

TenantCard.propTypes = {
  tenant: PropTypes.shape({
    tenant_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    tenant_name: PropTypes.string.isRequired,
    channels: PropTypes.array,
  }).isRequired,
};

/**
 * Canales page: lists every enabled tenant with all of their broadcast channels.
 */
function Canales({ tenants }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return tenants;
    const q = search.toLowerCase();
    return tenants.filter(t =>
      t.tenant_name.toLowerCase().includes(q) ||
      (t.channels || []).some(c => (c.name || '').toLowerCase().includes(q))
    );
  }, [tenants, search]);

  if (tenants.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0', color: '#9AA3B5', fontSize: '0.95rem' }}>
        No hay clientes disponibles.
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: 20,
        padding: '14px 18px',
        background: '#fff',
        borderRadius: 10,
        border: '1px solid #E2E5EC',
        boxShadow: '0 1px 3px rgba(2,19,68,0.06)',
      }}>
        <input
          type="text"
          placeholder="Buscar por cliente o canal…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...CONTROL_STYLE, minWidth: 220, flex: 1 }}
        />
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#9AA3B5', fontSize: '0.9rem' }}>
          Ningún cliente coincide con la búsqueda.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filtered.map(t => <TenantCard key={t.tenant_id} tenant={t} />)}
        </div>
      )}
    </div>
  );
}

Canales.propTypes = {
  tenants: PropTypes.arrayOf(PropTypes.shape({
    tenant_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    tenant_name: PropTypes.string.isRequired,
    channels: PropTypes.array,
  })).isRequired,
};

export default Canales;
